import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { Box, Divider, Icon, IconButton, Scrollbar, Typography } from 'shared/ui'
import LightBgPattern from 'shared/assets/images/placeholdersPattern/light_bg_pattern.png'
import DarkBgPattern from 'shared/assets/images/placeholdersPattern/dark_bg_pattern.png'
import { getCDNAssetURL } from 'shared/lib'
import { useSenderDetailsContext } from 'pages/settings/pages/rcsV2/pages/SenderDetails/hooks/useSenderDetailsContext'

import styles from './styles.module.scss'

export const RcsProfilePreview = observer(() => {
  const {
    brandInfoStore: { brandName, brandDescription },
    brandMediaStore: { logoUrl, accentColor, bannerUrl },
    brandContactsStore: { hasNumbers, hasWebsites, hasEmails, numbers, websites, emails },
  } = useSenderDetailsContext()

  return (
    <div className={styles.preview}>
      <div className={styles.preview__layer}>
        <div className={styles.preview__layerWrap}>
          <Scrollbar>
            <div className={styles.preview__header}>
              <IconButton
                color='tertiary'
                variant='icon'
                ariaLabel='back_to_lastStep'
                icon='arrowLeft'
                size='small'
              />
              <Typography
                ariaLabel='info_and_options'
                variant='body-sm-medium'
                color='var(--content-primary-primary)'
              >
                Info & options
              </Typography>
            </div>
            <div
              className={classNames(styles.preview__brandBackground, {
                [styles.preview__brandBackground_empty]: !bannerUrl,
              })}
              style={{ backgroundImage: `url(${bannerUrl || getCDNAssetURL(DarkBgPattern)})` }}
            >
              <div className={styles.preview__brandLogoWrap}>
                <div
                  className={classNames(styles.preview__brandLogo, {
                    [styles.preview__brandLogo_empty]: !logoUrl,
                  })}
                  style={{ backgroundImage: `url(${logoUrl || getCDNAssetURL(LightBgPattern)})` }}
                ></div>
              </div>
            </div>
            <div className={styles.preview__name}>
              <Typography
                ariaLabel='name'
                variant='body-rg-medium'
                color='var(--content-primary-primary)'
                className={styles.preview__textField}
              >
                {brandName || 'Display name'}
              </Typography>
              <Typography
                ariaLabel='name'
                className={styles.preview__textField}
                variant='body-sm-regular'
                color='var(--content-primary-secondary)'
              >
                {brandDescription || 'Description'}
              </Typography>
            </div>
            <Box
              display={'flex'}
              alignItems={'center'}
              justifyContent={'space-evenly'}
              padding={'8px 0'}
              minHeight={'52px'}
            >
              {hasNumbers && (
                <Box
                  display={'flex'}
                  alignItems={'center'}
                  gap={'5px'}
                  flexDirection={'column'}
                  width={'25%'}
                >
                  <Icon icon={'phone'} fontSize={16} color={accentColor} />
                  <Typography ariaLabel='number' variant='body-xs-medium' color={accentColor}>
                    Call
                  </Typography>
                </Box>
              )}
              {hasEmails && (
                <Box
                  display={'flex'}
                  alignItems={'center'}
                  gap={'5px'}
                  flexDirection={'column'}
                  width={'25%'}
                >
                  <Icon icon={'email'} fontSize={16} color={accentColor} />
                  <Typography ariaLabel='email' variant='body-xs-medium' color={accentColor}>
                    Email
                  </Typography>
                </Box>
              )}
              {hasWebsites && (
                <Box
                  display={'flex'}
                  alignItems={'center'}
                  gap={'5px'}
                  flexDirection={'column'}
                  width={'25%'}
                >
                  <Icon icon={'globe'} fontSize={16} color={accentColor} />
                  <Typography ariaLabel='website' variant='body-xs-medium' color={accentColor}>
                    Website
                  </Typography>
                </Box>
              )}
            </Box>
            <Box
              display={'flex'}
              alignItems={'center'}
              justifyContent={'space-evenly'}
              className={styles.preview__tabs}
            >
              <div className={styles.preview__tab} style={{ borderBottomColor: accentColor }}>
                <Typography
                  ariaLabel='name'
                  variant='body-xs-medium'
                  color='var(--content-primary-primary)'
                >
                  Info
                </Typography>
              </div>

              <div
                className={styles.preview__tab}
                style={{ borderBottomColor: 'var(--background-primary-inverted-primary)' }}
              >
                <Typography
                  ariaLabel='name'
                  variant='body-xs-medium'
                  color='var(--content-primary-disabled)'
                >
                  Options
                </Typography>
              </div>
            </Box>
            <Box display={'flex'} flexDirection={'column'} p={'20px 0 20px 16px'}>
              {hasNumbers && (
                <div className={styles.preview__item}>
                  {numbers.map((number) => (
                    <Box key={number.id} display={'flex'} alignItems={'flex-start'} gap={'14px'}>
                      <Icon
                        icon={'phone'}
                        fontSize={16}
                        color='var(--content-primary-secondary)'
                        className={styles.preview__icon}
                      />
                      <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
                        <Typography
                          ariaLabel='number'
                          variant='body-xs-regular'
                          color='var(--content-primary-primary)'
                        >
                          {number.phoneInputStore.number
                            ? `${number.phoneInputStore.country.labelRight} ${number.phoneInputStore.number}`
                            : 'Number'}
                        </Typography>
                        <Typography
                          ariaLabel='number'
                          variant='body-xs-regular'
                          color='var(--content-primary-secondary)'
                          width={'168px'}
                          ellipsis
                          tooltipProps={{
                            label: number.label,
                            placement: 'top',
                          }}
                        >
                          {number.label || 'Title'}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </div>
              )}
              {hasNumbers && (hasEmails || hasWebsites) && (
                <Divider sx={{ margin: '10px 0 14px 30px', borderColor: 'var(--black-20)' }} />
              )}
              {hasEmails && (
                <div className={styles.preview__item}>
                  {emails.map((email) => (
                    <Box key={email.id} display={'flex'} alignItems={'flex-start'} gap={'14px'}>
                      <Icon
                        icon={'email'}
                        fontSize={16}
                        color='var(--content-primary-secondary)'
                        className={styles.preview__icon}
                      />
                      <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
                        <Typography
                          ariaLabel='email'
                          variant='body-xs-regular'
                          color='var(--content-primary-primary)'
                          width={'168px'}
                          ellipsis
                          tooltipProps={{
                            label: email.email,
                            placement: 'top',
                          }}
                        >
                          {email.email || 'Email address'}
                        </Typography>
                        <Typography
                          ariaLabel='email'
                          variant='body-xs-regular'
                          color='var(--content-primary-secondary)'
                          width={'168px'}
                          ellipsis
                          tooltipProps={{
                            label: email.label,
                            placement: 'top',
                          }}
                        >
                          {email.label || 'Title'}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </div>
              )}
              {hasEmails && hasWebsites && (
                <Divider sx={{ margin: '10px 0 10px 30px', borderColor: 'var(--black-20)' }} />
              )}
              {hasWebsites && (
                <div className={styles.preview__item}>
                  {websites.map((website) => (
                    <Box key={website.id} display={'flex'} alignItems={'flex-start'} gap={'14px'}>
                      <Icon
                        icon={'globe'}
                        fontSize={16}
                        color='var(--content-primary-secondary)'
                        className={styles.preview__icon}
                      />
                      <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
                        <Typography
                          ariaLabel='website'
                          variant='body-xs-regular'
                          color='var(--content-primary-primary)'
                          width={'168px'}
                          ellipsis
                          tooltipProps={{
                            label: website.url,
                            placement: 'top',
                          }}
                        >
                          {website.url || 'URL address'}
                        </Typography>
                        <Typography
                          ariaLabel='website'
                          variant='body-xs-regular'
                          color='var(--content-primary-secondary)'
                          width={'168px'}
                          ellipsis
                          tooltipProps={{
                            label: website.label,
                            placement: 'top',
                          }}
                        >
                          {website.label || 'Title'}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </div>
              )}
            </Box>
          </Scrollbar>
        </div>
      </div>
    </div>
  )
})
